import React, { Component } from 'react';
import PropTypes from 'prop-types';

import PageSizeOptions from './PageSizeOptions';
import PageJump from './PageJump';
import TableButton from './TableButton';

class TablePagination extends Component {
  constructor(props) {
    super(props);
    this.state = {
      page: props.page,
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.page !== prevProps.page) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ page: this.props.page });
    }
  }

  getSafePage = page => {
    let newPage = page;
    const { pages } = this.props;
    if (Number.isNaN(page)) {
      newPage = this.props.page;
    }
    return Math.min(Math.max(newPage, 0), pages - 1);
  };

  getPageJumpProperties = () => {
    return {
      onKeyPress: e => {
        if (e.which === 13 || e.keyCode === 13) {
          this.applyPage();
        }
      },
      onBlur: this.applyPage,
      value: this.state.page === '' ? '' : this.state.page + 1,
      onChange: e => {
        const val = e.target.value;
        const page = val - 1;
        if (val === '') {
          this.setState({ page: val });
        }
        this.setState({ page: this.getSafePage(page) });
      },
      inputType: this.state.page === '' ? 'text' : 'number',
      pageJumpText: this.props.pageJumpText,
    };
  };

  changePage = page => {
    const newPage = this.getSafePage(page);
    this.setState({ page: newPage });
    if (this.props.page !== newPage) {
      this.props.onPageChange(newPage);
    }
  };

  applyPage = e => {
    if (e) {
      e.preventDefault();
    }
    const { page } = this.state;
    this.changePage(page === '' ? this.props.page : page);
  };

  handleGoToFirst = () => {
    if (!this.props.canPrevious) return;
    this.changePage(0);
  };

  handleGoToPrevious = () => {
    if (!this.props.canPrevious) return;
    this.changePage(this.props.page - 1);
  };

  handleGoToNext = () => {
    if (!this.props.canNext) return;
    this.changePage(this.props.page + 1);
  };

  handleGoToLast = () => {
    if (!this.props.canNext) return;
    this.changePage(this.props.pages || 1);
  };

  render() {
    const {
      FirstComponent,
      LastComponent,
      NextComponent,
      PreviousComponent,
      canPrevious,
      canNext,
      className,
      page,
      pageSizeOptions,
      pageSize,
      pages,
      showPageJump,
      showPageSizeOptions,
      style,
      onPageSizeChange,
      renderCurrentPage,
      renderPageJump,
      renderPageSizeOptions,
      renderTotalPagesCount,
    } = this.props;

    return (
      <div className={`${className} -pagination`} style={style}>
        <div className="-center">
          {showPageSizeOptions &&
            renderPageSizeOptions({
              pageSize,
              rowsSelectorText: this.props.rowsSelectorText,
              pageSizeOptions,
              onPageSizeChange,
              rowsText: this.props.rowsText,
            })}
          <span className="-pageInfo">
            {this.props.pageText}{' '}
            {showPageJump
              ? renderPageJump(this.getPageJumpProperties())
              : renderCurrentPage(page)}{' '}
            {this.props.ofText} {renderTotalPagesCount(pages)}
          </span>
        </div>
        <div className="-previous">
          <FirstComponent
            disabled={!canPrevious}
            onClick={this.handleGoToFirst}
          >
            {this.props.firstText}
          </FirstComponent>
        </div>
        <div className="-previous">
          <PreviousComponent
            disabled={!canPrevious}
            onClick={this.handleGoToPrevious}
          >
            {this.props.previousText}
          </PreviousComponent>
        </div>
        <div className="-next">
          <NextComponent disabled={!canNext} onClick={this.handleGoToNext}>
            {this.props.nextText}
          </NextComponent>
        </div>
        <div className="-next">
          <LastComponent disabled={!canNext} onClick={this.handleGoToLast}>
            {this.props.lastText}
          </LastComponent>
        </div>
      </div>
    );
  }
}

TablePagination.defaultProps = {
  FirstComponent: TableButton,
  LastComponent: TableButton,
  NextComponent: TableButton,
  PreviousComponent: TableButton,
  canNext: true,
  canPrevious: true,
  className: '',
  firstText: '',
  lastText: '',
  nextText: 'Next',
  ofText: 'of',
  page: 0,
  pageJumpText: '',
  pageSize: 10,
  pageSizeOptions: [5, 10, 20, 25, 50, 100],
  pageText: 'Page',
  pages: 1,
  previousText: 'Previous',
  renderCurrentPage: page => <span className="-currentPage">{page + 1}</span>,
  renderPageJump: PageJump,
  renderPageSizeOptions: args => <PageSizeOptions {...args} />,
  renderTotalPagesCount: pages => (
    <span className="-totalPages">{pages || 1}</span>
  ),
  rowsSelectorText: '',
  rowsText: 'rows',
  showPageJump: true,
  showPageSizeOptions: false,
  style: {},
  onPageChange: () => {},
  onPageSizeChange: () => {},
};

TablePagination.propTypes = {
  FirstComponent: PropTypes.func,
  LastComponent: PropTypes.func,
  NextComponent: PropTypes.func,
  PreviousComponent: PropTypes.func,
  canNext: PropTypes.bool,
  canPrevious: PropTypes.bool,
  className: PropTypes.string,
  firstText: PropTypes.any,
  lastText: PropTypes.any,
  nextText: PropTypes.any,
  ofText: PropTypes.string,
  page: PropTypes.number,
  pageJumpText: PropTypes.string,
  pageSize: PropTypes.number,
  pageSizeOptions: PropTypes.array,
  pageText: PropTypes.string,
  pages: PropTypes.number,
  previousText: PropTypes.shape({}),
  renderCurrentPage: PropTypes.func,
  renderPageJump: PropTypes.func,
  renderPageSizeOptions: PropTypes.func,
  renderTotalPagesCount: PropTypes.func,
  rowsSelectorText: PropTypes.string,
  rowsText: PropTypes.string,
  showPageJump: PropTypes.bool,
  showPageSizeOptions: PropTypes.bool,
  style: PropTypes.any,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
};

export default TablePagination;
